import { formatCurrency } from '@/app/utils/formatCurrency';
import { formatIsoDateOnlyShort } from '@/app/utils/formatDate';
import type { SoldAddOn, SoldItem, SoldService } from '../types/agreements.types';
import { scaledAmount } from './amountBasis';
import type { AmountBasis } from './amountBasis';
import { dueText } from './dueText';
import { recordedItems } from './invoiceableLines';
import type { InvoiceableService } from './invoiceableLines';
import { isAddOnSelectable, isItemSelectable, serviceCheckbox, unitKey } from './invoiceSelection';
import type { SelectionUnit, ServiceCheckbox } from './invoiceSelection';
import { isOverdue } from './overdue';
import { addOnLineScaled, fromScaled, itemLineScaled, toScaled } from './soldLineAmounts';
import { remainingServiceNetScaled } from './stillToInvoicePanel';

export type SelectionTone = 'selected' | 'partial' | 'none';

export interface ServiceSelectionRow {
    service: InvoiceableService;
    checkbox: ServiceCheckbox;
    tone: SelectionTone;
    note: string | null;
    selectionNote: string | null;
    net: string;
    fullNet: string | null;
    due: { date: string | null; mileage: string | null };
    overdue: boolean;
}

export interface ItemSelectionRow {
    item: SoldItem;
    unit: SelectionUnit;
    checked: boolean;
    selectable: boolean;
    tone: SelectionTone;
    net: string | null;
}

export interface AddOnSelectionRow {
    addOn: SoldAddOn;
    unit: SelectionUnit;
    checked: boolean;
    selectable: boolean;
    tone: SelectionTone;
    linkedTo: string | null;
    net: string;
    dueDate: string | null;
    overdue: boolean;
}

export const itemsSelectedNote = (selected: number, of: number): string => `${selected} of ${of} items selected`;

const itemUnit = (service: SoldService, item: SoldItem): SelectionUnit => ({
    kind: 'item',
    serviceId: service.id,
    itemId: item.id,
});

function partialSelection(
    service: InvoiceableService,
    selectedKeys: ReadonlySet<string>,
    flexible: boolean,
): { tone: SelectionTone; note: string | null } {
    if (!flexible) return { tone: 'none', note: null };

    const selectable = service.items.filter((item) => isItemSelectable(service, item, true));
    const selected = selectable.filter((item) => selectedKeys.has(unitKey(itemUnit(service, item)))).length;
    if (selected === 0) return { tone: 'none', note: null };

    return { tone: 'partial', note: itemsSelectedNote(selected, selectable.length) };
}

export function serviceSelectionRow(
    service: InvoiceableService,
    selectedKeys: ReadonlySet<string>,
    flexible: boolean,
    today: string,
    basis: AmountBasis = 'net',
): ServiceSelectionRow {
    const checkbox = serviceCheckbox(service, selectedKeys, flexible);
    const partial = checkbox.checked ? { tone: 'none' as const, note: null } : partialSelection(service, selectedKeys, flexible);

    return {
        service,
        checkbox,
        tone: checkbox.checked ? 'selected' : partial.tone,
        note: checkbox.note,
        selectionNote: partial.note,
        net: formatCurrency(fromScaled(basis === 'net' ? remainingServiceNetScaled(service) : toScaled(service.gross))),
        fullNet: service.partlyInvoiced ? `of ${formatCurrency(service.net)}` : null,
        due: dueText(service.dueDate, service.dueMileage),
        overdue: isOverdue(service.dueDate, today),
    };
}

export function itemSelectionRows(
    service: SoldService,
    selectedKeys: ReadonlySet<string>,
    flexible: boolean,
    { basis = 'net', priced = service.pricedAt === 'item' }: { basis?: AmountBasis; priced?: boolean } = {},
): ItemSelectionRow[] {
    const wholeServiceSelected = selectedKeys.has(unitKey({ kind: 'service', serviceId: service.id }));

    return recordedItems(service).map((item) => {
        const unit = itemUnit(service, item);
        const checked = wholeServiceSelected || selectedKeys.has(unitKey(unit));
        return {
            item,
            unit,
            checked,
            selectable: isItemSelectable(service, item, flexible),
            tone: checked ? 'selected' : 'none',
            net: priced ? formatCurrency(fromScaled(scaledAmount(itemLineScaled(item), basis))) : null,
        };
    });
}

export function addOnSelectionRow(
    addOn: SoldAddOn,
    selectedKeys: ReadonlySet<string>,
    serviceNames: ReadonlyMap<string, string>,
    today: string,
    basis: AmountBasis = 'net',
): AddOnSelectionRow {
    const unit: SelectionUnit = { kind: 'add_on', addOnId: addOn.id };
    const checked = selectedKeys.has(unitKey(unit));

    return {
        addOn,
        unit,
        checked,
        selectable: isAddOnSelectable(addOn),
        tone: checked ? 'selected' : 'none',
        linkedTo: addOn.linkedServiceId ? (serviceNames.get(addOn.linkedServiceId) ?? null) : null,
        net: formatCurrency(fromScaled(scaledAmount(addOnLineScaled(addOn), basis))),
        dueDate: addOn.dueDate ? formatIsoDateOnlyShort(addOn.dueDate) : null,
        overdue: isOverdue(addOn.dueDate, today),
    };
}
